import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDownTrayIcon, BookOpenIcon, BoltIcon } from "@heroicons/react/24/outline";
import Footer from "../../components/landing/Footer";
import LandingNavbar from "../../components/landing/LandingNavbar";
import usePageSeo from "../../hooks/usePageSeo";
import JsonLd from "../../components/seo/JsonLd";
import { buildCollectionPageSchema } from "../../data/seo/structuredData";
import { DOC_SECTIONS, TIER_META, docPath } from "../../content/docs/docsManifest";
import "../../styles/docs-print.css";

const PDFS = [
    {
        tier: "quickstart" as const,
        href: "/docs/pdf/flogpt-quick-start.pdf",
        title: "Quick Start Guide",
        blurb: "Every section's short path, illustrated. The one to hand a new team member.",
        icon: BoltIcon,
    },
    {
        tier: "manual" as const,
        href: "/docs/pdf/flogpt-complete-manual.pdf",
        title: "Complete Manual",
        blurb: "The full reference: every screen, field, and permission, with screenshots.",
        icon: BookOpenIcon,
    },
];

export default function DocsHubPage() {
    usePageSeo({
        title: "FloGPT Documentation — Get Started Guides & Manual | FloGPT",
        description:
            "Get started with FloGPT. Illustrated quick-start guides and a complete manual for the Dashboard and its analytics, AI Calls, Call Centre, Chat, Knowledge, Support, Users, Data, Integrations and Setup — readable online or downloadable as PDF.",
        keywords: "FloGPT documentation, FloGPT get started, AI voice agent setup guide, AI chat widget setup, call centre guide",
        canonical: "/docs",
    });

    const collectionSchema = useMemo(
        () =>
            buildCollectionPageSchema({
                name: "FloGPT Documentation",
                description:
                    "Get Started guides and the complete manual for the FloGPT platform: AI voice agents, chat widgets, the call centre, knowledge base, support inbox, integrations, analytics and account setup.",
                path: "/docs",
                itemCount: DOC_SECTIONS.length * 2,
            }),
        [],
    );

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100">
            <LandingNavbar />

            <main>
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-orange-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-pink-500/10 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-6xl px-6 py-16 lg:px-8 lg:py-20">
                        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
                            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                Documentation
                            </div>
                            <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl">Get started with FloGPT</h1>
                            <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-slate-300">
                                Every section of the product, documented twice: a short illustrated <strong className="text-white">Quick Start</strong> to get
                                you working, and a <strong className="text-white">Complete Manual</strong> for when you need the detail. Read online or download
                                the PDF.
                            </p>
                            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                                <Link
                                    to={docPath("quickstart", DOC_SECTIONS[0].slug)}
                                    className="inline-flex rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                >
                                    Start here
                                </Link>
                                <a
                                    href="#downloads"
                                    className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                >
                                    Download PDFs
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </section>

                <section id="downloads" className="border-b border-slate-900 py-14">
                    <div className="mx-auto max-w-6xl px-6 lg:px-8">
                        <h2 className="text-2xl font-semibold text-white">Download</h2>
                        <p className="mt-2 text-slate-400">Both PDFs are generated from the pages on this site, so they never drift from the web version.</p>
                        <div className="mt-6 grid gap-4 sm:grid-cols-2">
                            {PDFS.map((pdf) => {
                                const Icon = pdf.icon;
                                return (
                                    <a
                                        key={pdf.tier}
                                        href={pdf.href}
                                        className="group flex items-start gap-4 rounded-2xl border border-slate-800 bg-slate-900/50 p-5 transition hover:border-slate-700 hover:bg-slate-900"
                                    >
                                        <span className="rounded-xl bg-gradient-to-br from-orange-500/20 to-pink-500/20 p-3">
                                            <Icon className="h-6 w-6 text-orange-400" />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="flex items-center gap-2 font-semibold text-white">
                                                {pdf.title}
                                                <ArrowDownTrayIcon className="h-4 w-4 text-slate-500 transition group-hover:text-orange-400" />
                                            </span>
                                            <span className="mt-1 block text-sm text-slate-400">{pdf.blurb}</span>
                                        </span>
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section className="py-14">
                    <div className="mx-auto max-w-6xl px-6 lg:px-8">
                        <h2 className="text-2xl font-semibold text-white">Browse by section</h2>
                        <p className="mt-2 text-slate-400">
                            Section names match the sidebar in your dashboard, so what you read here is what you click there.
                        </p>

                        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {DOC_SECTIONS.map((section) => {
                                const Icon = section.icon;
                                return (
                                    <div
                                        key={section.slug}
                                        className="flex flex-col rounded-2xl border border-slate-800 bg-slate-900/40 p-5 transition hover:border-slate-700"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="rounded-lg bg-slate-800/80 p-2">
                                                <Icon className="h-5 w-5 text-orange-400" />
                                            </span>
                                            <h3 className="font-semibold text-white">{section.title}</h3>
                                            {section.status === "outline" ? (
                                                <span className="ml-auto rounded border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                                                    Outline
                                                </span>
                                            ) : null}
                                        </div>
                                        <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{section.summary}</p>
                                        {section.roleNote ? <p className="mt-3 text-xs text-slate-500">{section.roleNote}</p> : null}
                                        <div className="mt-4 flex gap-2">
                                            <Link
                                                to={docPath("quickstart", section.slug)}
                                                className="flex-1 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-center text-xs font-medium text-slate-200 transition hover:bg-slate-800"
                                            >
                                                {TIER_META.quickstart.label}
                                            </Link>
                                            <Link
                                                to={docPath("manual", section.slug)}
                                                className="flex-1 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-center text-xs font-medium text-slate-200 transition hover:bg-slate-800"
                                            >
                                                {TIER_META.manual.label}
                                            </Link>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section className="border-t border-slate-900 py-14">
                    <div className="mx-auto max-w-6xl px-6 lg:px-8">
                        <h2 className="text-2xl font-semibold text-white">Integration guides</h2>
                        <p className="mt-2 text-slate-400">Platform-specific setup that goes beyond the in-app sections.</p>
                        <Link
                            to="/docs/shopify"
                            className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/50 p-5 transition hover:border-slate-700 hover:bg-slate-900"
                        >
                            <span className="font-semibold text-white">FloGPT for Shopify</span>
                            <span className="text-sm text-slate-400">Install, OAuth scopes, storefront widget, webhooks, GDPR</span>
                        </Link>
                    </div>
                </section>
            </main>

            <Footer />
            <JsonLd id="collection-docs" data={collectionSchema} />
        </div>
    );
}
